import React, { useEffect, useMemo, useState } from 'react'
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '../utils/firebase'
import { useAuth } from '../contexts/AuthContext'
import { Users, Shield, Award, Phone, Mail } from 'lucide-react'
import PaywallModal from '../components/PaywallModal'
import './PeoplePage.css'

/**
 * Public resident view for the neighborhood's leadership rosters:
 * Board Members, Neighborhood Admins, and Committee Chairs.
 *
 * Source of truth mirrors the admin surface at Admin → Neighborhood
 * Profile (see NeighborhoodProfileFields in AdminPage.jsx). Directory-
 * flagged users (`isBoardMember` / `isAdmin`) are merged with the
 * admin-managed arrays on the neighborhood doc, deduped by name.
 *
 * The footer links to `/people#board`, `/people#admins`, and
 * `/people#committee` — hash anchors auto-scroll to each section.
 */
function PeoplePage() {
  const { userProfile, isSubscriptionActive } = useAuth()
  const [loading, setLoading] = useState(true)
  const [board, setBoard] = useState([])
  const [admins, setAdmins] = useState([])
  const [committee, setCommittee] = useState([])
  const [neighborhoodName, setNeighborhoodName] = useState('')

  const isAdmin = !!(userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin)
  const neighborhoodId = userProfile?.neighborhoodId

  useEffect(() => {
    if (!neighborhoodId) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      try {
        const [neighSnap, boardSnap, adminSnap] = await Promise.all([
          getDoc(doc(db, 'neighborhoods', neighborhoodId)),
          getDocs(query(
            collection(db, 'users'),
            where('neighborhoodId', '==', neighborhoodId),
            where('isBoardMember', '==', true)
          )),
          getDocs(query(
            collection(db, 'users'),
            where('neighborhoodId', '==', neighborhoodId),
            where('isAdmin', '==', true)
          )),
        ])
        if (cancelled) return

        const neighData = neighSnap.exists() ? neighSnap.data() : {}
        setNeighborhoodName(neighData.name || '')

        const normalizePeople = (arr) => (Array.isArray(arr) ? arr : []).map(p => ({
          name: (p?.name || '').trim(),
          title: (p?.title || '').trim(),
          email: (p?.email || '').trim(),
          phone: (p?.phone || '').trim(),
          source: 'manual',
        })).filter(p => p.name)

        const userToRow = (u, titleField) => ({
          name: [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.name || '',
          title: (u[titleField] || '').trim(),
          email: (u.showEmail !== false ? u.email : '') || '',
          phone: (u.showPhone !== false ? u.phone : '') || '',
          uid: u.uid,
          profilePhotoURL: u.profilePhotoURL || '',
          source: 'directory',
        })

        // Merge directory-flagged users with the manual list; dedupe
        // by lowercase name so an admin who's also listed manually
        // doesn't show twice.
        const dedupe = (rows) => {
          const seen = new Set()
          return rows.filter(r => {
            const key = r.name.toLowerCase()
            if (!key || seen.has(key)) return false
            seen.add(key)
            return true
          })
        }

        const boardDirRows = boardSnap.docs.map(d => userToRow({ uid: d.id, ...d.data() }, 'boardTitle'))
        const adminDirRows = adminSnap.docs.map(d => userToRow({ uid: d.id, ...d.data() }, 'adminTitle'))

        setBoard(dedupe([...boardDirRows, ...normalizePeople(neighData.boardMembers)]))
        setAdmins(dedupe([...adminDirRows, ...normalizePeople(neighData.neighborhoodAdmins)]))
        setCommittee(normalizePeople(neighData.committeeChairs))
      } catch (e) {
        console.error('Error loading people:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [neighborhoodId])

  // Auto-scroll to hash anchor on mount / hash change so the footer
  // links land on the right section.
  useEffect(() => {
    const scrollToHash = () => {
      const h = window.location.hash.replace('#', '')
      if (!h) return
      const el = document.getElementById(h)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    // Delay so the sections have rendered.
    if (!loading) setTimeout(scrollToHash, 100)
    window.addEventListener('hashchange', scrollToHash)
    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [loading])

  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'

  const totalPeople = board.length + admins.length + committee.length

  return (
    <div className="people-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      <header className="people-header">
        <h1>People</h1>
        <p className="muted">
          {loading
            ? `Loading…`
            : `${totalPeople} ${totalPeople === 1 ? 'person' : 'people'} in ${locationLabel}`}
        </p>
      </header>

      <PeopleSection
        id="board"
        title="Board Members"
        icon={<Award size={20} />}
        description="Elected officers and non-resident advisors serving the board."
        people={board}
        loading={loading}
      />

      <PeopleSection
        id="admins"
        title="Neighborhood Admins"
        icon={<Shield size={20} />}
        description="Residents responsible for day-to-day administration."
        people={admins}
        loading={loading}
      />

      <PeopleSection
        id="committee"
        title="Committee Chairs"
        icon={<Users size={20} />}
        description="Chairs of neighborhood committees (social, architectural, etc.)."
        people={committee}
        loading={loading}
      />
    </div>
  )
}

function PeopleSection({ id, title, icon, description, people, loading }) {
  return (
    <section id={id} className="people-section">
      <div className="people-section-header">
        <div className="people-section-title">
          <span className="people-section-icon">{icon}</span>
          <h2>{title}</h2>
        </div>
        <p className="muted">{description}</p>
      </div>

      {loading ? (
        <div className="card people-empty">
          <p className="muted">Loading…</p>
        </div>
      ) : people.length === 0 ? (
        <div className="card people-empty">
          <p className="muted">No {title.toLowerCase()} listed yet.</p>
        </div>
      ) : (
        <div className="people-grid">
          {people.map((p, i) => (
            <PersonCard key={`${p.name}-${i}`} person={p} />
          ))}
        </div>
      )}
    </section>
  )
}

function PersonCard({ person }) {
  const initials = (person.name || '?')
    .split(/\s+/)
    .map(w => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  const formatPhone = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(-10)
    if (digits.length !== 10) return raw || ''
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  return (
    <div className="card person-card">
      <div className="avatar xl">
        {person.profilePhotoURL
          ? <img src={person.profilePhotoURL} alt={person.name} />
          : initials}
      </div>
      <h3 className="person-name">{person.name}</h3>
      {person.title && <p className="person-title muted">{person.title}</p>}
      <div className="person-contacts">
        {person.email && (
          <a
            href={`mailto:${person.email}`}
            className="btn-ghost small person-contact-btn"
            title={person.email}
          >
            <Mail size={14} />
            <span>Email</span>
          </a>
        )}
        {person.phone && (
          <a
            href={`tel:${String(person.phone).replace(/\D/g, '')}`}
            className="btn-ghost small person-contact-btn"
            title={formatPhone(person.phone)}
          >
            <Phone size={14} />
            <span>{formatPhone(person.phone)}</span>
          </a>
        )}
      </div>
    </div>
  )
}

export default PeoplePage
